import { useState } from "react";
import { Phone, MessageCircle, Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { SITE } from "../lib/site";
import { useSettings } from "../context/SettingsContext";
import SectionHeading from "../components/SectionHeading";
import { MaskedLines, FadeIn, Reveal } from "../components/motion/Reveal";
import PageMeta from "../components/PageMeta";

const Contact = () => {
  const site = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/\S+@\S+\.\S+/.test(form.email) || !form.message.trim()) {
      toast.error("Please fill name, a valid email, and your message");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll reply within 24 hours");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.data?.detail || err.message || "Something went wrong. Please try again.";
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageMeta
        title="Contact Us — Factory Direct Inquiries"
        description="Get in touch with Pickleballgearz in Sialkot, Pakistan. Contact our production team for custom quotes, private label manufacturing, or factory visits."
      />
      <section className="border-b border-black/10 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 pb-16 pt-16 md:pt-24 lg:px-10">
          <FadeIn>
            <p className="mono-caption mb-8 text-neutral-500">Contact Us</p>
          </FadeIn>
          <h1 className="font-display text-4xl font-semibold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["TALK TO THE", <>FACTORY<span className="text-brand-red">.</span></>]} />
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 py-20 md:py-28 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <SectionHeading number="01" title="Reach Us" className="!mb-10" />
            <div className="space-y-px border border-black/10">
              {[
                { icon: Phone, label: "Phone", value: site.phone, href: site.phoneLink, tid: "contact-phone" },
                { icon: MessageCircle, label: "WhatsApp", value: "Chat with our export team", href: site.whatsappLink, tid: "contact-whatsapp" },
                { icon: Mail, label: "Primary Email", value: site.email, href: `mailto:${site.email}`, tid: "contact-email" },
                ...(site.secondaryEmail ? [{ icon: Mail, label: "Direct Support (Gmail)", value: site.secondaryEmail, href: `mailto:${site.secondaryEmail}`, tid: "contact-secondary-email" }] : []),
                { icon: MapPin, label: "Address", value: site.address, href: null, tid: "contact-address" },
              ].map((c) => (
                <Reveal key={c.label}>
                  {c.href ? (
                    <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={c.tid} className="group flex items-start gap-4 bg-white p-6 transition-colors hover:bg-brand-ink">
                      <c.icon size={20} className="mt-1 shrink-0 text-brand-red" />
                      <span>
                        <span className="mono-caption block text-neutral-400 group-hover:text-white/50">{c.label}</span>
                        <span className="mt-1 block font-medium group-hover:text-white">{c.value}</span>
                      </span>
                    </a>
                  ) : (
                    <div className="flex items-start gap-4 bg-white p-6" data-testid={c.tid}>
                      <c.icon size={20} className="mt-1 shrink-0 text-brand-red" />
                      <span>
                        <span className="mono-caption block text-neutral-400">{c.label}</span>
                        <span className="mt-1 block font-medium">{c.value}</span>
                      </span>
                    </div>
                  )}
                </Reveal>
              ))}
            </div>

            <Reveal className="mt-10">
              <div className="relative overflow-hidden border border-black/10 bg-brand-smoke h-64" data-testid="contact-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d54238.41160359876!2d74.49887754687501!3d32.502901300000006!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391eebf05050a257%3A0x2f1f5163158c3!2sSialkot%2C%20Punjab%2C%20Pakistan!5e0!3m2!1sen!2s!4v1708845012345!5m2!1sen!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: "grayscale(1) contrast(1.2)" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="absolute inset-0 h-full w-full"
                  title="Sialkot, Pakistan Map"
                ></iframe>
                <div className="absolute bottom-4 left-4 bg-brand-ink/90 px-3 py-1.5 backdrop-blur-sm">
                  <span className="mono-caption text-white text-[10px] tracking-wider uppercase">Sialkot, Punjab — Pakistan</span>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-3">
            <SectionHeading number="02" title="Send a Message" className="!mb-10" />
            <Reveal>
              <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2" data-testid="contact-form">
                <div>
                  <label className="label-pp" htmlFor="ct-name">Name *</label>
                  <input id="ct-name" className="input-pp" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" />
                </div>
                <div>
                  <label className="label-pp" htmlFor="ct-email">Email *</label>
                  <input id="ct-email" type="email" className="input-pp" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="label-pp" htmlFor="ct-phone">Phone</label>
                  <input id="ct-phone" className="input-pp" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone-input" />
                </div>
                <div>
                  <label className="label-pp" htmlFor="ct-subject">Subject</label>
                  <input id="ct-subject" className="input-pp" value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-subject" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-pp" htmlFor="ct-message">Message *</label>
                  <textarea id="ct-message" rows={6} className="input-pp !h-auto py-3" value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" />
                </div>
                <div className="sm:col-span-2">
                  <button type="submit" disabled={sending} className="btn-primary disabled:opacity-50" data-testid="contact-submit-btn">
                    {sending ? "Sending..." : "Send Message"} <Send size={15} />
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
